import React, { useContext, useEffect, useState } from "react";
import { ThemeContext } from "../theme";
import { Play, Users, Zap, Trophy, Clock } from "lucide-react";

const steps = [
    {
        icon: Users,
        title: "Create or Join a Game",
        description:
            "Start a new game and invite friends with a link, or join an existing game using a game code.",
    },
    {
        icon: Play,
        title: "Choose a Letter",
        description:
            "Each round begins with a randomly selected letter. All answers must start with that letter.",
    },
    {
        icon: Clock,
        title: "Fill the Categories",
        description:
            "You’ll see a list of categories. Quickly type valid answers before time runs out.",
    },
    {
        icon: Zap,
        title: "Submit Before Time Ends",
        description:
            "Speed matters. The faster you submit, the better your chances of scoring higher.",
    },
    {
        icon: Trophy,
        title: "Score & Win",
        description:
            "Unique and valid answers earn points. Compete across rounds and climb the leaderboard.",
    },
];

const HowToPlay = () => {
    const { colors } = useContext(ThemeContext)!;
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        setVisible(true);
    }, []);

    return (
        <section className={`min-h-screen ${colors.bgSecondaryColor} px-6 py-6 md:py-20`}>
            <div className="mx-auto max-w-6xl">
                {/* Header */}
                <div
                    className={`text-center mb-6 md:mb-16 transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                        }`}
                >
                    <h1 className={`text-4xl md:text-5xl font-bold ${colors.textColor}`}>
                        How to Play
                    </h1>
                    <p className={`${colors.textColor} opacity-70 mt-4 max-w-2xl mx-auto`}>
                        Learn the rules of I Call On in under a minute and jump straight into the action.
                    </p>
                </div>

                {/* Steps */}
                <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                    {steps.map((step, index) => {
                        const Icon = step.icon;
                        return (
                            <div
                                key={index}
                                className={`${colors.cardBg} ${colors.borderColor} border rounded-2xl p-8 shadow-lg hover:scale-105 transition-transform`}
                            >
                                <div className="mb-6 flex items-center gap-4">
                                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600">
                                        <Icon className="h-6 w-6 text-white" />
                                    </div>
                                    <span className="text-sm font-semibold text-blue-500">
                                        Step {index + 1}
                                    </span>
                                </div>

                                <h3 className={`text-2xl font-bold ${colors.textColor} mb-3`}>
                                    {step.title}
                                </h3>
                                <p className={`${colors.textColor} opacity-70`}>
                                    {step.description}
                                </p>
                            </div>
                        );
                    })}
                </div>

                {/* Tips */}
                <div
                    className={`mt-6 md:mt-20 ${colors.cardBg} ${colors.borderColor} border rounded-2xl p-10 shadow-lg`}
                >
                    <h2 className={`text-3xl font-bold ${colors.textColor} mb-6 text-center`}>
                        Pro Tips
                    </h2>
                    <ul className={`${colors.textColor} opacity-80 space-y-4 max-w-3xl mx-auto`}>
                        <li>⚡ Unique answers score more than common ones.</li>
                        <li>⏱️ Don’t overthink — speed is part of the challenge.</li>
                        <li>🧠 Creative thinking beats memorization.</li>
                        <li>👥 Playing with more friends makes the game more competitive.</li>
                    </ul>
                </div>

                {/* CTA */}
                <div className="mt-20 text-center">
                    <a
                        href="/download"
                        className="inline-block rounded-xl bg-blue-600 px-10 py-4 text-lg font-semibold text-white hover:bg-blue-700 transition"
                    >
                        Start Playing Now
                    </a>
                </div>
            </div>
        </section>
    );
};

export default HowToPlay;
